import { DatePipe, DecimalPipe } from "@angular/common";
import { Component } from "@angular/core";

import { SaldosService } from "src/app/core/services/saldos.service";
import { CitaService } from "src/app/core/services/cita.service";
import { MovimientoPagos } from "src/app/core/models/saldo.model";

type Filtro = "TODOS" | "PAGOS" | "SALDO";

@Component({
  selector: "app-mis-pagos",
  standalone: true,
  imports: [DatePipe, DecimalPipe],
  templateUrl: "./mis-pagos.component.html",
  styleUrl: "../../app.component.scss",
})
export class MisPagosComponent {
  currentUser: any;
  saldo: number = 0;
  movimientos: MovimientoPagos[] = [];
  cargando: boolean = true;
  error: boolean = false;
  filtro: Filtro = "TODOS";

  /** Citas de los movimientos, indexadas por id, para mostrar de qué cita salió cada uno */
  citasPorId: Record<string, any> = {};

  constructor(
    private __saldosService: SaldosService,
    private __citaService: CitaService
  ) {}

  ngOnInit(): void {
    const currentUser = localStorage.getItem("currentPatient");
    if (currentUser) {
      this.currentUser = JSON.parse(currentUser);
    }
    this.getMisPagos(this.currentUser.userId);
  }

  /**
   * Una sola llamada: el endpoint ya devuelve pagos y movimientos de saldo
   * unificados y ordenados por fecha descendente.
   */
  async getMisPagos(idPaciente: string) {
    this.cargando = true;
    this.error = false;
    try {
      const respuesta = await this.__saldosService.getMisPagos(idPaciente);
      this.saldo = respuesta?.saldo ?? 0;
      this.movimientos = respuesta?.movimientos ?? [];
      await this.cargarCitasDeLosMovimientos();
    } catch (error) {
      console.error("Error consultando el historial de pagos:", error);
      this.error = true;
    } finally {
      this.cargando = false;
    }
  }

  /**
   * Trae en UNA sola consulta las citas de todos los movimientos, para poder
   * mostrar fecha, hora, especialista y consultorio en vez del id.
   *
   * Si falla no se rompe la pantalla: sin citas, cada movimiento cae en su
   * descripción de siempre.
   */
  async cargarCitasDeLosMovimientos() {
    const ids = Array.from(
      new Set(this.movimientos.map((m) => m.idCita).filter((id): id is string => !!id))
    );
    if (!ids.length) {
      return;
    }
    try {
      const citas = await this.__citaService.getCitasByIds(ids);
      this.citasPorId = citas.reduce((mapa, cita) => {
        mapa[cita.$id] = cita;
        return mapa;
      }, {} as Record<string, any>);
    } catch (error) {
      console.error("No se pudieron cargar las citas de los movimientos:", error);
    }
  }

  citaDe(mov: MovimientoPagos): any | null {
    return mov.idCita ? this.citasPorId[mov.idCita] ?? null : null;
  }

  /** "Online" no tiene consultorio; en sede presencial sí */
  consultorioDe(mov: MovimientoPagos): string | null {
    const cita = this.citaDe(mov);
    if (!cita || cita?.idSede?.sede === "Online") {
      return null;
    }
    return cita.consultorio || null;
  }

  especialistaDe(mov: MovimientoPagos): string | null {
    return this.citaDe(mov)?.idEspecialista?.nombre ?? null;
  }

  sedeDe(mov: MovimientoPagos): string | null {
    return this.citaDe(mov)?.idSede?.sede ?? null;
  }

  /**
   * Título del movimiento. Cuando se conoce la cita se describe el hecho y los
   * datos de la cita van aparte; el id crudo no le dice nada al paciente.
   */
  tituloDe(mov: MovimientoPagos): string {
    if (!this.citaDe(mov)) {
      return mov.descripcion;
    }
    if (mov.tipo === "saldo") {
      return mov.origen === "cancelacion"
        ? "Saldo a favor por la cita cancelada"
        : "Saldo aplicado a esta cita";
    }
    return this.esPagoConSaldo(mov) ? "Cita pagada con tu saldo" : "Pago de la cita";
  }

  get movimientosFiltrados(): MovimientoPagos[] {
    if (this.filtro === "PAGOS") {
      return this.movimientos.filter((m) => m.tipo === "pago");
    }
    if (this.filtro === "SALDO") {
      return this.movimientos.filter((m) => m.tipo === "saldo");
    }
    return this.movimientos;
  }

  cambiarFiltro(filtro: Filtro) {
    this.filtro = filtro;
  }

  /** Una cita cubierta con saldo no es un cobro real a la tarjeta */
  esPagoConSaldo(mov: MovimientoPagos): boolean {
    return mov.tipo === "pago" && mov.banco === "SALDO_A_FAVOR";
  }

  /**
   * Etiqueta y estilo del estado. Mismo criterio que mis-paquetes.
   */
  resolverEstado(mov: MovimientoPagos) {
    if (mov.tipo === "saldo") {
      return mov.valor >= 0
        ? { texto: "Saldo a favor acreditado", clase: "bg-success" }
        : { texto: "Saldo aplicado a una cita", clase: "bg-secondary" };
    }

    if (this.esPagoConSaldo(mov)) {
      return { texto: "Pagado con saldo a favor", clase: "bg-info" };
    }

    switch ((mov.estado ?? "").toString()) {
      case "aprobada":
      case "aprobado":
      case "pagada":
        return { texto: "Pago aprobado", clase: "bg-success" };
      case "creada":
      case "pendiente":
        return { texto: "Pendiente de pago", clase: "bg-warning" };
      case "rechazada":
      case "rechazado":
        return { texto: "Pago rechazado", clase: "bg-danger" };
      default:
        return { texto: "Sin estado", clase: "bg-secondary" };
    }
  }

  /**
   * OJO: en /getMisPagos los items tipo "saldo" YA vienen con signo
   * (negativo = débito). No se les vuelve a aplicar el signo aquí.
   */
  iconoMovimiento(mov: MovimientoPagos): string {
    if (mov.tipo === "saldo") {
      return mov.valor >= 0 ? "fas fa-plus-circle" : "fas fa-minus-circle";
    }
    return this.esPagoConSaldo(mov) ? "fas fa-wallet" : "fas fa-credit-card";
  }

  claseValor(mov: MovimientoPagos): string {
    if (mov.tipo === "saldo") {
      return mov.valor >= 0 ? "text-success" : "text-muted";
    }
    return "text-dark";
  }

  /**
   * @param date
   * @returns
   */
  convertToLocal(date: string | Date): Date {
    const utcDate = new Date(date);
    const localDate = new Date(utcDate.getTime() - -5 * 60 * 60 * 1000); // UTC-5
    return localDate;
  }
}
